import { useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { useGasAppCalls } from './gas-application-calls.hook'
import { MIN_MILISECONDS_REFRESH_GAS_APPS } from '../constants'
import { setApplicationsData, resetApplications } from '../slices'
import { setApplication, setFailure } from '../slices/gas-applications.slice'

export const useGasAppData = () => {
  const dispatch = useDispatch()
  const {
    getGasApps,
    getGasApp,
    cancelPut,
    uploadDocument,
    reviewCall,
    rollbackPut,
    cancelInstaller,
  } = useGasAppCalls()
  const loaded = useSelector((state) => state.gasApplications.msLoaded)
  const applications = useSelector((state) => state.gasApplications.applications)
  const userId = useSelector((state) => state.global.authData.user_id)
  const isAuthorized = useSelector((state) => state.global.authData.isAuthorized) // RUTA CORREGIDA
  const cancelPendingApplications = useSelector(
    (state) => state.gasApplications.pendingApplications
  )

  const updateGasApps = useCallback(() => {
    if (!userId || !isAuthorized) {
      return
    }
    if (loaded && Date.now() - loaded < MIN_MILISECONDS_REFRESH_GAS_APPS) {
      return
    }
    getGasApps(userId)
      .then((data) => {
        dispatch(setApplicationsData(data))
      })
      .catch(() => dispatch(setFailure()))
  }, [dispatch, setApplicationsData, getGasApps, userId, loaded, isAuthorized])

  const resetGasApps = useCallback(() => {
    dispatch(resetApplications())
  }, [dispatch, resetApplications])

  useEffect(() => {
    if (!loaded) {
      updateGasApps()
    }
  }, [loaded, updateGasApps])

  const cancelApplication = useCallback(
    (application, payload) =>
      new Promise(async (resolve, reject) => {
        try {
          await cancelPut(application['id'], payload)
          const dataGas = await getGasApp(application.id)
          dispatch(setApplication(dataGas))
          resolve(dataGas)
        } catch (response) {
          return reject(response)
        }
      }),
    [dispatch, getGasApp, cancelPut]
  )

  const cancelGuild = useCallback(
    (application, payload) =>
      new Promise(async (resolve, reject) => {
        try {
          await cancelInstaller(application.id, payload)
          const dataGas = await getGasApp(application.id)
          dispatch(setApplication(dataGas))
          resolve(dataGas)
        } catch (response) {
          return reject(response)
        }
      }),
    [dispatch, getGasApp, cancelInstaller]
  )

  const rollbackApp = useCallback(
    (application, payload) => {
      return new Promise(async (resolve, reject) => {
        try {
          await rollbackPut(application['id'], payload)
          const dataGas = await getGasApp(application.id)
          dispatch(setApplication(dataGas))
          resolve(dataGas)
        } catch (response) {
          return reject(response)
        }
      })
    },
    [dispatch, getGasApp, rollbackPut]
  )

  const requestReview = useCallback(
    (id, payload, file) =>
      new Promise(async (resolve, reject) => {
        try {
          if (file) {
            const document = {
              documento: file.data.split(',')[1],
              nombre: file.name.replace(/\.[^/.]+$/, ''),
              tipo: 'FC',
              extension: file.type,
              cliente_id: parseInt(userId),
              solicitud_id: id.toString(),
              save_event: false,
            }
            const { data } = await uploadDocument(document)
            payload['list_doc_id'] = data['id'].toString()
          }
          await reviewCall(id, payload)
          const dataGas = await getGasApp(id)
          dispatch(setApplication(dataGas))
          resolve(dataGas)
        } catch (response) {
          return reject(response)
        }
      }),
    [dispatch, getGasApp, reviewCall, uploadDocument, userId]
  )

  return {
    cancelPendingApplications,
    loaded,
    applications,
    updateGasApps,
    resetGasApps,
    cancelApplication,
    rollbackApp,
    requestReview,
    cancelGuild,
  }
}
